import { useEffect } from "react";
import axios from "axios";
import { Divider } from "@mui/material";
import localize from "../../common/localize";
import useGlobalContext from "../../hooks/useGlobalContext";
import { getClearDeviceSetup } from "../../common/EntitiesManagement";
import FieldCheckbox from "../../components/common/FieldCheckbox";
import FieldTextField from "../../components/common/FieldTextfield";
import FieldSlider from "../../components/common/FieldSlider";
import FieldColor from "../../components/common/FieldColor";
import ExpandableView from "../../components/common/ExpandableView";

export default function DeviceSetup({ newSetup, setNewSetup, showModal }) {
  const { DEVICE_SETUP_API, config } = useGlobalContext();

  const getDeviceSetup = async () => {
    const deviceSetupResult = await axios.get(`${DEVICE_SETUP_API}`, config);
    if (deviceSetupResult?.data?.Ok && deviceSetupResult.data.Data) {
      return deviceSetupResult.data.Data;
    }

    return null;
  };

  const handleTextFieldChange = (e) => {
    setNewSetup({
      ...newSetup,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckedChanged = (e) => {
    setNewSetup({
      ...newSetup,
      [e.target.name]: e.target.checked,
    });
  };

  const handleMillisFields = (e) => {
    if (e.target.value && e.target.value !== "") {
      setNewSetup({
        ...newSetup,
        [e.target.name]: e.target.value * 1000,
      });
    }
  };

  const requestDeviceSetup = async () => {
    const requestResult = await getDeviceSetup();
    if (requestResult) {
      setNewSetup({
        ...requestResult,
      });
      return;
    }

    setNewSetup(getClearDeviceSetup());
  };

  useEffect(() => {
    if (showModal) {
      requestDeviceSetup();
    }
  }, [showModal]);

  const getSecondsFromMillis = (millis) => (millis ? millis / 1000 : 0);

  return (
    <div style={{ width: "100%", height: "100%", marginTop: "2px !important" }}>
      {newSetup && (
        <ExpandableView
          components={[
            {
              Name: localize.general_information,
              Component: (
                <div className="flex-col">
                  <FieldTextField
                    isForDrawer
                    isRequired
                    fieldName="ApplicationBaseUrl"
                    fieldTitle={localize.application_base_url}
                    fieldSubtitle={localize.application_base_url_description}
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.ApplicationBaseUrl}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    isRequired
                    fieldName="SignInBaseUrl"
                    fieldTitle={localize.sign_in_base_url}
                    fieldSubtitle={localize.sign_in_base_url_description}
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.SignInBaseUrl}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MinimumBatteryLevel"
                    fieldTitle={localize.minimum_battery_level}
                    fieldSubtitle={localize.minimum_battery_level_description}
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MinimumBatteryLevel}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MinimumTimeBetweenGPSUpdatesInMillis"
                    fieldTitle={localize.minimum_time_between_gps_updates}
                    fieldSubtitle={
                      localize.minimum_time_between_gps_updates_description
                    }
                    handleValueChange={handleMillisFields}
                    fieldValue={getSecondsFromMillis(
                      newSetup?.MinimumTimeBetweenGPSUpdatesInMillis
                    )}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MinimumDistanceChangeInMeters"
                    fieldTitle={localize.minimum_distance_change_in_meters}
                    fieldSubtitle={
                      localize.minimum_distance_change_in_meters_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MinimumDistanceChangeInMeters}
                    customMinWidth="50%"
                  />
                </div>
              ),
            },
            {
              Name: localize.events,
              Component: (
                <div className="flex-col">
                  <FieldCheckbox
                    isForDrawer
                    fieldName="AutoAssign"
                    valueChecked={newSetup?.AutoAssign}
                    valueOnChecked={localize.yes}
                    valueOnUnchecked={localize.no}
                    handleCheckedChanged={handleCheckedChanged}
                    fieldTitle={localize.visit_auto_assign}
                    fieldSubtitle={localize.visit_auto_assign_description}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="TimeToRequestEventsInMillis"
                    fieldTitle={localize.time_to_request_events}
                    fieldSubtitle={localize.time_to_request_events_description}
                    handleValueChange={handleMillisFields}
                    fieldValue={getSecondsFromMillis(
                      newSetup?.TimeToRequestEventsInMillis
                    )}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="SynchronizationIntervalInMillis"
                    fieldTitle={localize.synchronization_interval}
                    fieldSubtitle={
                      localize.synchronization_interval_description
                    }
                    handleValueChange={handleMillisFields}
                    fieldValue={getSecondsFromMillis(
                      newSetup?.SynchronizationIntervalInMillis
                    )}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="DaysToShowEvents"
                    fieldTitle={localize.daysToShowEvents}
                    fieldSubtitle={localize.daysToShowEventsDescription}
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.DaysToShowEvents}
                    customMinWidth="50%"
                  />
                </div>
              ),
            },
            {
              Name: localize.evidences,
              Component: (
                <div className="flex-col">
                  <FieldTextField
                    isForDrawer
                    fieldName="MaximumEvidencesPerEvent"
                    fieldTitle={localize.maximum_photo_evidences_per_event}
                    fieldSubtitle={
                      localize.maximum_photo_evidences_per_event_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MaximumEvidencesPerEvent}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MaximumVideoEvidencesPerEvent"
                    fieldTitle={localize.maximum_video_evidences_per_event}
                    fieldSubtitle={
                      localize.maximum_video_evidences_per_event_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MaximumVideoEvidencesPerEvent}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MaximumAudioEvidencesPerEvent"
                    fieldTitle={localize.maximum_audio_evidences_per_event}
                    fieldSubtitle={
                      localize.maximum_audio_evidences_per_event_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MaximumAudioEvidencesPerEvent}
                    customMinWidth="50%"
                  />

                  <Divider />
                  <FieldTextField
                    isForDrawer
                    fieldName="MaximumAudioTimeInSeconds"
                    fieldTitle={localize.maximum_audio_time}
                    fieldSubtitle={localize.maximum_audio_time_description}
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.MaximumAudioTimeInSeconds}
                    customMinWidth="50%"
                  />
                </div>
              ),
            },
            {
              Name: localize.additional_data,
              Component: (
                <div className="flex-col">
                  <FieldCheckbox
                    customMinWidth="50%"
                    isForDrawer
                    fieldName="ShowGPSValue"
                    valueChecked={newSetup?.ShowGPSValue}
                    valueOnChecked={localize.yes}
                    valueOnUnchecked={localize.no}
                    handleCheckedChanged={handleCheckedChanged}
                    fieldTitle={localize.show_gps_value}
                    fieldSubtitle={localize.show_gps_value_description}
                  />
                  <Divider />
                  <FieldCheckbox
                    customMinWidth="50%"
                    isForDrawer
                    fieldName="ShowGPSValueAsDegreesMinutesSeconds"
                    valueChecked={newSetup?.ShowGPSValueAsDegreesMinutesSeconds}
                    valueOnChecked={localize.degrees_minuets_seconds}
                    valueOnUnchecked={localize.latitude_and_longitude}
                    handleCheckedChanged={handleCheckedChanged}
                    fieldTitle={localize.show_gps_degrees_minutes_seconds}
                    fieldSubtitle={
                      localize.show_gps_degress_minutes_seconds_description
                    }
                  />
                  <Divider />
                  <FieldCheckbox
                    customMinWidth="50%"
                    isForDrawer
                    fieldName="ShowGPSCity"
                    valueChecked={newSetup?.ShowGPSCity}
                    valueOnChecked={localize.yes}
                    valueOnUnchecked={localize.no}
                    handleCheckedChanged={handleCheckedChanged}
                    fieldTitle={localize.show_gps_city}
                    fieldSubtitle={localize.show_gps_city_description}
                  />
                  <Divider />
                  <FieldSlider
                    customMinWidth="50%"
                    isForDrawer
                    fieldName="EvidenceCompression"
                    fieldTitle={localize.field_evidence_compression}
                    fieldSubtitle={
                      localize.field_evidence_compression_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.EvidenceCompression ?? 80}
                  />
                  <Divider />
                  <FieldColor
                    customMinWidth="50%"
                    isForDrawer
                    fieldName="GpsLabelTextColor"
                    fieldTitle={localize.field_gps_label_text_color}
                    fieldSubtitle={
                      localize.field_gps_label_text_color_description
                    }
                    handleValueChange={handleTextFieldChange}
                    fieldValue={newSetup?.GpsLabelTextColor ?? "#FFFFFF"}
                  />
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
